// frontend/src/pages/OrderConfirmation.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FiCheckCircle,
  FiPackage,
  FiTruck,
  FiMapPin,
  FiDownload,
  FiShoppingBag,
  FiMail,
  FiAlertCircle,
} from "react-icons/fi";
import api from "../services/api";
import { formatPrice } from "../utils/format";
import { useAuth } from "../context/AuthContext";
import { trackMarketingEvent } from "../utils/marketingTracking";
import "../styles/OrderConfirmation.css";

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const orderId = searchParams.get("orderId");
  const paymentSuccess = searchParams.get("payment_success") === "1";
  const paymentCancelled = searchParams.get("paymentCancelled") === "1";
  const paymentError = searchParams.get("payment_error") === "1";

  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reportedPurchase = useRef(null);

  // ✅ Role-aware destination
  const ordersDestination =
    user?.role === "Admin" ? "/admin/orders" : "/orders";

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError("No order ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get("/api/orders");
        const found = (response.data.orders || []).find(
          (o) => o.OrderNumber === orderId,
        );

        if (!found) {
          setError("Order not found");
          setLoading(false);
          return;
        }

        setOrder(found);

        try {
          const detailRes = await api.get(`/api/orders/${found.OrderID}`);
          setItems(detailRes.data.items || []);
        } catch (itemErr) {
          console.warn("Could not load order items:", itemErr.message);
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        setError(err.response?.data?.message || "Failed to load order");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    const paidStatuses = new Set(["paid", "completed", "succeeded", "success"]);
    const orderKey = order?.OrderID;
    if (
      !orderKey ||
      !paidStatuses.has(String(order.PaymentStatus || "").toLowerCase()) ||
      reportedPurchase.current === orderKey
    ) {
      return;
    }
    reportedPurchase.current = orderKey;
    trackMarketingEvent("purchase", {
      orderId: orderKey,
      value: Number(order.TotalAmount || 0),
      currency: "ZAR",
      orderNumber: order.OrderNumber,
    });
  }, [order]);

  const renderStatusBanner = () => {
    if (paymentSuccess) {
      return (
        <div className="oc-banner oc-banner-success">
          <FiCheckCircle size={22} />
          <div>
            <strong>Payment received!</strong>
            <span>Your order has been confirmed and is being processed.</span>
          </div>
        </div>
      );
    }
    if (paymentCancelled) {
      return (
        <div className="oc-banner oc-banner-warn">
          <FiAlertCircle size={22} />
          <div>
            <strong>Payment was cancelled</strong>
            <span>Your order is pending. You can retry the payment.</span>
          </div>
        </div>
      );
    }
    if (paymentError) {
      return (
        <div className="oc-banner oc-banner-error">
          <FiAlertCircle size={22} />
          <div>
            <strong>Payment failed</strong>
            <span>Something went wrong. Please try again.</span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="oc-page">
        <div className="oc-loading">
          <div className="oc-spinner" />
          <p>Loading your order…</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="oc-page">
        <div className="oc-error-card">
          <FiAlertCircle size={48} color="#ef4444" />
          <h2>Order not found</h2>
          <p>{error || "We couldn't locate that order."}</p>
          <button onClick={() => navigate("/shop")} className="oc-btn-primary">
            <FiShoppingBag /> Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const shipping = (() => {
    try {
      return typeof order.ShippingAddress === "string"
        ? JSON.parse(order.ShippingAddress)
        : order.ShippingAddress || {};
    } catch {
      return {};
    }
  })();

  return (
    <div className="oc-page">
      <div className="oc-container">
        <div className="oc-header">
          <div className="oc-check-icon">
            <FiCheckCircle />
          </div>
          <h1>Thank you for your order! 🎉</h1>
          <p className="oc-subtitle">
            A confirmation email has been sent to{" "}
            <strong>{shipping.email || "your email"}</strong>.
          </p>
          <div className="oc-order-number">
            Order Number: <strong>{order.OrderNumber}</strong>
          </div>
        </div>

        {renderStatusBanner()}

        <div className="oc-grid">
          <div className="oc-card">
            <div className="oc-card-header">
              <FiPackage /> Order Status
            </div>
            <div className="oc-status-badge">{order.Status || "Pending"}</div>
            <p className="oc-meta">
              Payment:{" "}
              <strong>
                {order.PaymentGateway
                  ? `${order.PaymentGateway.toUpperCase()} — ${
                      order.PaymentStatus || "Pending"
                    }`
                  : `${order.PaymentMethod || "N/A"} — ${
                      order.PaymentStatus || "Pending"
                    }`}
              </strong>
            </p>
            <p className="oc-meta">
              Placed: {new Date(order.CreatedAt).toLocaleString()}
            </p>
          </div>

          <div className="oc-card">
            <div className="oc-card-header">
              <FiTruck /> Shipping To
            </div>
            <p className="oc-meta">
              <strong>
                {shipping.firstName} {shipping.lastName}
              </strong>
            </p>
            <p className="oc-meta">{shipping.address}</p>
            <p className="oc-meta">
              {shipping.city}
              {shipping.postalCode ? `, ${shipping.postalCode}` : ""}
            </p>
            {shipping.phone && (
              <p className="oc-meta">
                <strong>Phone:</strong> {shipping.phone}
              </p>
            )}
          </div>
        </div>

        <div className="oc-card oc-card-wide">
          <div className="oc-card-header">
            <FiShoppingBag /> Items ({items.length})
          </div>
          <div className="oc-items">
            {items.length === 0 ? (
              <p className="oc-meta">No item details available.</p>
            ) : (
              items.map((item) => (
                <div
                  key={item.OrderItemID || item.ProductID}
                  className="oc-item"
                >
                  <div className="oc-item-img">
                    {item.ImageUrl ? (
                      <img src={item.ImageUrl} alt={item.Name} />
                    ) : (
                      <div className="oc-item-img-placeholder">📦</div>
                    )}
                  </div>
                  <div className="oc-item-info">
                    <h4>{item.Name}</h4>
                    {item.SKU && (
                      <span className="oc-item-sku">SKU: {item.SKU}</span>
                    )}
                    <div className="oc-item-meta">
                      <span>Qty: {item.Quantity}</span>
                      <span>× {formatPrice(item.UnitPrice)}</span>
                    </div>
                  </div>
                  <div className="oc-item-price">
                    {formatPrice(item.TotalPrice)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="oc-totals">
            <div className="oc-total-row">
              <span>Subtotal</span>
              <span>{formatPrice(order.Subtotal || 0)}</span>
            </div>
            {order.TaxAmount > 0 && (
              <div className="oc-total-row">
                <span>VAT/Tax</span>
                <span>{formatPrice(order.TaxAmount)}</span>
              </div>
            )}
            <div className="oc-total-row">
              <span>Shipping</span>
              <span>{formatPrice(order.ShippingFee || 0)}</span>
            </div>
            <div className="oc-total-row oc-total-grand">
              <span>Total</span>
              <span>{formatPrice(order.TotalAmount || 0)}</span>
            </div>
          </div>
        </div>

        <div className="oc-actions">
          <button
            onClick={() => navigate("/shop")}
            className="oc-btn-secondary"
          >
            <FiShoppingBag /> Continue Shopping
          </button>
          <button
            onClick={() => navigate(ordersDestination)}
            className="oc-btn-primary"
          >
            <FiPackage /> View My Orders
          </button>
          <a
            href={`/api/dashboard/${order.OrderID}/invoice`}
            target="_blank"
            rel="noreferrer"
            className="oc-btn-outline"
          >
            <FiDownload /> Download Invoice
          </a>
        </div>

        <div className="oc-help">
          <FiMail /> Need help? Contact us — we're happy to assist.
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
